import React from 'react'
import classnames from 'classnames/bind'

import { Container } from '@caudena/core/ui-kit'
import Form, { Field, validators } from '@caudena/core/Form'
import { MagicGrid } from '@caudena/core/ui-kit'
import { Label } from '@caudena/core/ui-kit'
import { RadioButtonGroup } from '@caudena/core/ui-kit'
import { Switch } from '@caudena/core/ui-kit'
import { Checkbox } from '@caudena/core/ui-kit'

import RuleSeverityFilter from './RuleSeverityFilter'
import RuleRiskAmountFilter from './RuleRiskAmountFilter'
import RuleScoreFilter from './RuleScoreFilter'
import CreateRuleConfirmationButton from '../CreateRuleConfirmationButton'

import { NewRuleApiParams } from '../../../../types/Rule'

import styles from './index.scss'

const cx = classnames.bind(styles)

const directionOptions = [
  { value: 'both', label: 'In / Out' },
  { value: 'in', label: 'Incoming' },
  { value: 'out', label: 'Outgoing' },
]

const initialFormValues = {
  type: 'score',
  direction: 'both',
  riskAmount: '',
  severity: 'HIGH',
  autoBlock: false,
  retrospectivelyApply: false,
  retrospectivelyBlock: false,
  scoreAmount: '',
  usd: false,
  score: false,
}

interface CreateFallbackRuleFormProps {
  onSubmit: (data) => Promise<any>
  onSubmitModalOpen: (isOpen: boolean) => void
}

const CreateFallbackRuleForm: React.FC<CreateFallbackRuleFormProps> = ({
  onSubmit,
  onSubmitModalOpen,
}) => {
  const formRef = React.useRef<HTMLFormElement>(null)

  const handleSubmit = (data) => {
    const submitData: NewRuleApiParams = {
      type: data.type,
      severity: data.severity,
      direction: data.direction,
      usd: data.usd,
      usd_amount: data.riskAmount,
      score: data.score,
      score_amount: data.scoreAmount,
      block_client: data.autoBlock,
      run_retrospectivly: data.retrospectivelyApply,
      block_old_clients: data.retrospectivelyBlock,
    }

    return onSubmit(submitData).then(() => formRef.current?.form.restart())
  }

  React.useEffect(() => {
    const unsubscribe = formRef.current?.form.subscribe(
      ({ values: { retrospectivelyApply } }) => {
        if (!retrospectivelyApply) {
          formRef.current?.form.change('retrospectivelyBlock', false)
        }
      },
      { values: true }
    )

    return () => {
      unsubscribe()
    }
  }, [])

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={initialFormValues}
      ref={formRef as any}
    >
      {({ form }) => {
        const { values, valid } = form.getState()
        const { usd, score, retrospectivelyApply } = values

        return (
          <Container gap={[1, 0]}>
            <MagicGrid gap={2.5}>
              <Label content="Transaction direction">
                <Field
                  name="direction"
                  as={RadioButtonGroup}
                  options={directionOptions}
                  fullWidth
                />
              </Label>
              <Label
                as={'div'}
                content={<Field as={Checkbox} name="score" label="Score" />}
              >
                <Field
                  name="scoreAmount"
                  disabled={!score}
                  validate={(value, allValues, meta) => {
                    if (allValues.score)
                      return validators.required()(value, allValues, meta)
                  }}
                  as={RuleScoreFilter}
                />
              </Label>
              <Label
                as={'div'}
                content={<Field as={Checkbox} name="usd" label="Risk amount" />}
              >
                <Field
                  name="riskAmount"
                  disabled={!usd}
                  validate={(value, allValues, meta) => {
                    if (allValues.usd)
                      return validators.required()(value, allValues, meta)
                  }}
                  as={RuleRiskAmountFilter}
                />
              </Label>
              <Label content="Severity">
                <Field name="severity" as={RuleSeverityFilter} />
              </Label>
              <Container gap={[1.5, 1.5]} className={cx('SwitchGroup')}>
                <MagicGrid>
                  <Field
                    name="autoBlock"
                    as={Switch}
                    type="single"
                    label="Automatically block client when the rule is triggered"
                  />
                  <Field
                    name="retrospectivelyApply"
                    as={Switch}
                    type="single"
                    label="Apply the rule retrospectively"
                  />
                  <Field
                    name="retrospectivelyBlock"
                    as={Switch}
                    type="single"
                    label="Block client when the rule is triggered in retrospect"
                    disabled={!retrospectivelyApply}
                  />
                </MagicGrid>
              </Container>
              <CreateRuleConfirmationButton
                onModalOpen={onSubmitModalOpen}
                values={values}
                valid={valid}
                submit={() => form.submit()}
              />
            </MagicGrid>
          </Container>
        )
      }}
    </Form>
  )
}

export default CreateFallbackRuleForm
